/**
 * IndexedDB-backed cache for the "Listen" button on the For You feed.
 *
 * Premium users can listen to their personalized daily narration offline
 * for the last 7 days. We cache the data-URI audio by date (YYYY-MM-DD)
 * scoped to user_id so switching accounts on a shared device doesn't leak.
 *
 * Schema: db 'lalaland-audio' object store 'foryou' keyPath 'key' where
 * `key = `${user_id}:${date}` for uniqueness across accounts.
 */

const DB_NAME = "lalaland-audio";
const DB_VERSION = 1;
const STORE = "foryou";
const MAX_DAYS = 7;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function todayKey() {
  // UTC date to match backend's `date_key = now.strftime("%Y-%m-%d")`.
  return new Date().toISOString().slice(0, 10);
}

/** Save an audio entry. Also prunes anything older than 7 days for this user. */
export async function saveAudio({ user_id, date, audio_url, script, voice, provider }) {
  if (!user_id || !audio_url) return;
  const db = await openDb();
  const key = `${user_id}:${date}`;
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put({
      key,
      user_id,
      date,
      audio_url,
      script: script || "",
      voice: voice || null,
      provider: provider || null,
      saved_at: new Date().toISOString(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  // Prune (fire-and-forget)
  pruneOld(user_id).catch(() => {});
}

/** Fetch a cached entry. Returns null if missing. */
export async function getAudio(user_id, date = todayKey()) {
  if (!user_id) return null;
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(`${user_id}:${date}`);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch (_err) {
    return null;
  }
}

/** Return last N entries for this user, newest first. */
export async function recentAudio(user_id, limit = MAX_DAYS) {
  if (!user_id) return [];
  try {
    const db = await openDb();
    const all = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    return all
      .filter((e) => e.user_id === user_id)
      .sort((a, b) => (a.date < b.date ? 1 : -1))
      .slice(0, limit);
  } catch (_err) {
    return [];
  }
}

/** Delete entries older than MAX_DAYS for this user. Keeps storage lean. */
async function pruneOld(user_id) {
  const db = await openDb();
  const cutoff = new Date(Date.now() - MAX_DAYS * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const all = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
  const stale = all.filter((e) => e.user_id === user_id && e.date < cutoff);
  if (!stale.length) return;
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    stale.forEach((e) => store.delete(e.key));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export const TODAY_KEY = todayKey;
